/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/ProgramContent.module.css';
import { useMemo } from 'react';
// --- Types ---

interface ProgramContentProps {
	modules: ModuleType[];
	loading?: boolean;
	error?: any;
	showMarkCompleteButton?: boolean;
}

export interface ModuleType {
	id: string;
	sequence: number;
	title: string;
	startdate: string;
	enddate: string;
	isCompleted: boolean;
	description?: string;
	totalActivities?: number;
	completedActivities?: number;
	lessons?: LessonType[]; // ✅ ADD THIS
}

export interface LessonType {
	id: string;
	sequence: number;
	title: string;
	date: string;
	dateTimeStr: string;
	type: string;
	typeColor?: string;
	participants: string[];
	isCompleted: boolean;
	duration?: number;
	description?: string;
}

// --- SVG Icons ---
const CheckIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="#00875A"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
		<polyline points="22 4 12 14.01 9 11.01"></polyline>
	</svg>
);

const CrossIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="#D92D20"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="12" cy="12" r="10"></circle>
		<line x1="15" y1="9" x2="9" y2="15"></line>
		<line x1="9" y1="9" x2="15" y2="15"></line>
	</svg>
);

const CalendarIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={styles.icon}
	>
		<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
		<line x1="16" y1="2" x2="16" y2="6"></line>
		<line x1="8" y1="2" x2="8" y2="6"></line>
		<line x1="3" y1="10" x2="21" y2="10"></line>
	</svg>
);

const ChevronDownIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={styles.icon}
	>
		<polyline points="6 9 12 15 18 9"></polyline>
	</svg>
);

const ChevronUpIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={styles.icon}
	>
		<polyline points="18 15 12 9 6 15"></polyline>
	</svg>
);


export const formatModuleDate = (dateString: string) => {
	if (!dateString) return '';

	return new Date(dateString).toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: '2-digit',
	});
};

export const formatActivityDateTime = (
	startDateTime: string,
	endDateTime: string
) => {
	if (!startDateTime || !endDateTime) return '';

	const start = new Date(startDateTime);
	const end = new Date(endDateTime);

	const day = start.toLocaleDateString('en-GB', {
		weekday: 'long',
	});

	const date = start.toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	});

	const startTime = start.toLocaleTimeString('en-GB', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: 'Asia/Kolkata',
	});

	const endTime = end.toLocaleTimeString('en-GB', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: 'Asia/Kolkata',
	});

	return `${day} | ${date} | ${startTime} - ${endTime} IST`;
};
// --- Subcomponents ---

const ActivityItem: React.FC<{
	lesson: LessonType;
	onMarkComplete?: (lessonId: string, isCompleted: boolean) => void;
	showMarkCompleteButton: boolean;
}> = ({ lesson, onMarkComplete, showMarkCompleteButton }) => {
	const [isExpanded, setIsExpanded] = useState(true);

	return (
		<div className={styles.activityCard}>
			<div
				className={styles.activityHeader}
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<div className={styles.activityTitleGroup}>
					<span className={styles.activitySequence}>
						Activity {lesson.sequence}
					</span>
					<span className={styles.activityTitle}>{lesson.title}</span>
					{lesson.isCompleted ? <CheckIcon /> : <CrossIcon />}
				</div>
				<div className={styles.activityDateGroup}>
					<CalendarIcon />
					<span> <strong></strong>{lesson.date}</span>
					{isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</div>
			</div>

			{isExpanded && (
				<div className={styles.activityBody}>
					<div className={styles.activityMetaRow}>
						<span className={styles.dateTimeStr}>
							{lesson.dateTimeStr}
						</span>
						<div className={styles.typeTag}>
							<span
								className={styles.orangeSquare}
								style={{
									backgroundColor: lesson.typeColor,
								}}
							></span>
							{lesson.type}
						</div>
					</div>


					<div className={styles.actionRow}>

						<div className={styles.participantsSection}>
							<p className={styles.participantsLabel}>
								Activity Description
							</p>
							<div className={styles.participantsList}>
								{lesson.description
									? lesson.description
									: "No description available"}
							</div>
						</div>
{showMarkCompleteButton && (
						<button
							className={`${styles.markCompleteBtn} ${lesson.isCompleted
								? styles.markIncompleteBtn
								: styles.markCompleteBtnGreen
								}`}
							onClick={() =>
								onMarkComplete?.(lesson.id, lesson.isCompleted)
							}
						>
							{lesson.isCompleted ? (
								<>
									Mark as Incomplete <CrossIcon />
								</>
							) : (
								<>
									Mark as Complete <CheckIcon />
								</>
							)}

						</button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

const ModuleItem: React.FC<{
	moduleData: ModuleType;
	onMarkComplete?: (lessonId: string, isCompleted: boolean) => void;
	showMarkCompleteButton: boolean;
}> = ({ moduleData, onMarkComplete, showMarkCompleteButton }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const allActivitiesCompleted =
		moduleData.lessons &&
		moduleData.lessons.length > 0 &&
		moduleData.lessons.every(lesson => lesson.isCompleted);

	const totalActivities = moduleData.lessons?.length || 0;

	const completedActivities =
		moduleData.lessons?.filter(lesson => lesson.isCompleted).length || 0;

	return (
		<div className={styles.moduleItem}>
			<div
				className={styles.moduleHeader}
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<div className={styles.moduleTitleGroup}>
					<span className={styles.moduleSequence}>
						Module {moduleData.sequence}
					</span>
					<span className={styles.moduleTitle}>
						{moduleData.title}
					</span>
					{allActivitiesCompleted ? <CheckIcon /> : <CrossIcon />}
				</div>

				<div className={styles.moduleDateGroup}>
					<CalendarIcon />
					<span>
						<strong>{formatModuleDate(moduleData.startdate)}</strong>
						{' '}
						to
						{' '}
						<strong>{formatModuleDate(moduleData.enddate)}</strong>
					</span>
					{isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</div>
			</div>

			{isExpanded && (
				<div className={styles.moduleBody}>
					{moduleData.description && (
						<p className={styles.moduleDescription}>
							{moduleData.description}
						</p>
					)}

					<div className={styles.activitiesHeaderRow}>
						<h4>Activities</h4>

						<span className={styles.activitiesCount}>
							{completedActivities}/{totalActivities} Activities Completed
						</span>
					</div>

					<div className={styles.activitiesList}>
						{moduleData.lessons?.map(lesson => (
							<ActivityItem
								key={lesson.id}
								lesson={lesson}
								onMarkComplete={onMarkComplete}
								showMarkCompleteButton={showMarkCompleteButton}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

// --- Main Component ---
console.log('📦 ProgramContent received modules 👉', module);

export default function ProgramContentProgramContent({
	modules,
	loading,
	error,
	onMarkComplete,
	showMarkCompleteButton = true,
}: ProgramContentProps & {
	onMarkComplete?: (lessonId: string, isCompleted: boolean) => void;
}) {
	const { totalModules, totalActivities, formattedDuration } = useMemo(() => {
		if (!modules || modules.length === 0) {
			return {
				totalModules: 0,
				totalActivities: 0,
				formattedDuration: '0h 0m',
			};
		}

		const totalModules = modules.length;

		const totalActivities = modules.reduce<number>(
			(acc, mod) => acc + (mod.lessons?.length || 0),
			0
		);

		const totalMinutes = modules.reduce((acc, mod) => {
			return (
				acc +
				(mod.lessons?.reduce(
					(lessonAcc, lesson) => lessonAcc + (lesson.duration || 0),
					0
				) || 0)
			);
		}, 0);

		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		return {
			totalModules,
			totalActivities,
			formattedDuration: `${hours}h ${minutes}m`,
		};
	}, [modules]);

	return (
		<div className={styles.container}>
			<header className={styles.topHeader}>
				<h2>Program Content</h2>
				<div className={styles.metaData}>
					<span>{totalModules} Modules</span>
					<span className={styles.dot}>•</span>
					<span>{totalActivities} Activities</span>
					<span className={styles.dot}>•</span>
					<span>{formattedDuration} Total Duration</span>
				</div>
			</header>

			<div className={styles.modulesContainer}>
				{loading && (
					<p className={styles.loadingText}>Loading modules...</p>
				)}
				{error && (
					<p className={styles.errorText}>Error loading modules.</p>
				)}

				{!loading &&
					!error &&
					modules.map(mod => (
						<ModuleItem
							key={mod.id}
							moduleData={mod}
							onMarkComplete={onMarkComplete}
							showMarkCompleteButton={showMarkCompleteButton}
						/>
					))}
			</div>
		</div>
	);
}
